"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.whatsappTemplateSchema = exports.TEMPLATE_STATUSES = exports.TEMPLATE_PURPOSES = exports.rateCardSchema = exports.listItemSchema = exports.LIST_KINDS = exports.deliveryRulesSchema = exports.failureReasonSchema = void 0;
exports.aedToFils = aedToFils;
exports.filsToAed = filsToAed;
exports.priceForWeight = priceForWeight;
exports.codFee = codFee;
exports.templatePlaceholders = templatePlaceholders;
exports.fillTemplate = fillTemplate;
// Rules, lists, rate cards and WhatsApp templates (plan 4.1). Money is always whole fils.
const zod_1 = require("zod");
// ---------------------------------------------------------------- money
/** "12.5" / "12.50" / 12.5 → 1250 fils. Rejects more than 2 decimals and negatives. */
function aedToFils(value) {
    const s = String(value).trim().replace(/,/g, '');
    if (!/^\d+(\.\d{1,2})?$/.test(s))
        throw new Error(`Not an AED amount: ${value}`);
    const [whole, frac = ''] = s.split('.');
    return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}
/** 1250 → "12.50" */
function filsToAed(fils) {
    const sign = fils < 0 ? '-' : '';
    const abs = Math.abs(Math.trunc(fils));
    return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}
const aed = zod_1.z
    .union([zod_1.z.string(), zod_1.z.number()])
    .transform((v, ctx) => {
    try {
        return aedToFils(v);
    }
    catch {
        ctx.addIssue({ code: 'custom', message: 'AED_FORMAT' });
        return zod_1.z.NEVER;
    }
});
/** Price for one parcel: the highest slab the weight reaches, else the base price. */
function priceForWeight(card, weightGrams) {
    if (weightGrams == null)
        return card.pricePerParcelFils;
    const slab = [...card.slabs].sort((a, b) => b.fromGrams - a.fromGrams).find((s) => weightGrams >= s.fromGrams);
    return slab ? slab.priceFils : card.pricePerParcelFils;
}
/** COD fee: fixed fils plus a percentage (basis points) of the COD amount, rounded to whole fils. */
function codFee(card, codAmountFils) {
    if (codAmountFils <= 0)
        return 0;
    return card.codFeeFils + Math.round((codAmountFils * card.codFeePercentBp) / 10_000);
}
// ---------------------------------------------------------------- schemas
const code = zod_1.z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{1,31}$/, 'CODE_FORMAT');
const reason = zod_1.z.string().trim().max(500).optional();
exports.failureReasonSchema = zod_1.z.object({
    code,
    name: zod_1.z.string().trim().min(3).max(120),
    requiresPhoto: zod_1.z.boolean().default(true),
    requiresCallProof: zod_1.z.boolean().default(true),
    isActive: zod_1.z.boolean().default(true),
    sortOrder: zod_1.z.coerce.number().int().min(0).max(999).default(0),
    reason,
});
exports.deliveryRulesSchema = zod_1.z.object({
    maxAttempts: zod_1.z.coerce.number().int().min(1).max(10),
    autoRtoAfterMaxAttempts: zod_1.z.boolean(),
    callsRequired: zod_1.z.coerce.number().int().min(0).max(10),
    minMinutesBetweenCalls: zod_1.z.coerce.number().int().min(0).max(120),
    whatsappOnFailure: zod_1.z.boolean(),
    gpsPhotoOnFailure: zod_1.z.boolean(),
    podPhotosRequired: zod_1.z.coerce.number().int().min(0).max(5),
    decisionDeadlineHour: zod_1.z.coerce.number().int().min(0).max(23),
    escalateToOpsAfterHours: zod_1.z.coerce.number().int().min(1).max(168),
    reason,
});
exports.LIST_KINDS = ['PAYMENT_METHOD', 'DELIVERY_TYPE', 'TIME_SLOT'];
const hhmm = zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'TIME_FORMAT');
exports.listItemSchema = zod_1.z
    .object({
    kind: zod_1.z.enum(exports.LIST_KINDS),
    code,
    name: zod_1.z.string().trim().min(2).max(80),
    isEnabled: zod_1.z.boolean().default(true),
    startTime: hhmm.nullish(),
    endTime: hhmm.nullish(),
    sortOrder: zod_1.z.coerce.number().int().min(0).max(999).default(0),
    reason,
})
    .refine((v) => v.kind !== 'TIME_SLOT' || (v.startTime && v.endTime && v.startTime < v.endTime), {
    message: 'TIME_SLOT_NEEDS_START_BEFORE_END',
    path: ['endTime'],
});
exports.rateCardSchema = zod_1.z.object({
    projectId: zod_1.z.uuid(),
    merchantId: zod_1.z.uuid().nullish(),
    /** YYYY-MM-DD */
    effectiveFrom: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'DATE_FORMAT'),
    pricePerParcel: aed,
    codFee: aed.default(0),
    codFeePercent: zod_1.z.coerce.number().min(0).max(100).default(0),
    returnFee: aed.default(0),
    failedAttemptFee: aed.default(0),
    slabs: zod_1.z
        .array(zod_1.z.object({ fromKg: zod_1.z.coerce.number().positive().max(1000), price: aed }))
        .max(20)
        .default([]),
    notes: zod_1.z.string().trim().max(1000).nullish(),
});
exports.TEMPLATE_PURPOSES = ['OUT_FOR_DELIVERY', 'FAILED_ATTEMPT', 'LOCATION_REQUEST', 'STATUS_ALERT', 'BULK_NOTICE'];
exports.TEMPLATE_STATUSES = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED'];
/** Placeholders in a WhatsApp body: {{1}}, {{2}}… must be numbered from 1 without gaps (Meta rule). */
function templatePlaceholders(body) {
    return [...new Set([...body.matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1])))].sort((a, b) => a - b);
}
/** Fills {{n}} with the given values (preview, and later the message text). */
function fillTemplate(body, values) {
    return body.replace(/\{\{(\d+)\}\}/g, (_, n) => values[Number(n) - 1] ?? `{{${n}}}`);
}
exports.whatsappTemplateSchema = zod_1.z
    .object({
    code,
    metaName: zod_1.z.string().trim().regex(/^[a-z0-9_]{3,60}$/, 'META_NAME_FORMAT'),
    purpose: zod_1.z.enum(exports.TEMPLATE_PURPOSES),
    body: zod_1.z.string().trim().min(10).max(1024),
    variables: zod_1.z.array(zod_1.z.string().trim().min(2).max(60)).max(10).default([]),
    status: zod_1.z.enum(exports.TEMPLATE_STATUSES).default('DRAFT'),
    isActive: zod_1.z.boolean().default(true),
    reason,
})
    .refine((v) => {
    const p = templatePlaceholders(v.body);
    return p.every((n, i) => n === i + 1) && p.length === v.variables.length;
}, { message: 'PLACEHOLDERS_MUST_MATCH_VARIABLES', path: ['variables'] });
//# sourceMappingURL=rules.js.map